import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useAttentionIndicators } from "@/hooks/useAttentionIndicators";
import axiosInstance, { baseURL } from "@/utils/appsettings";
import { claimEffect, playChime, readMuted, writeMuted } from "@/lib/attentionEffects";
import {
  applyEvent,
  attentionLine,
  AttentionNotification,
  AttentionStreamEvent,
  displayTitle,
  fromSnapshot,
  projectIdFromPath,
} from "@/lib/workflowAttention";

interface WorkflowAttentionValue {
  notifications: AttentionNotification[];
  muted: boolean;
  setMuted: (muted: boolean) => void;
  openNotification: (notification: AttentionNotification) => void;
  clearAll: () => void;
}

const NOOP: WorkflowAttentionValue = {
  notifications: [],
  muted: false,
  setMuted: () => {},
  openNotification: () => {},
  clearAll: () => {},
};

const WorkflowAttentionContext = createContext<WorkflowAttentionValue>(NOOP);

// Used when the server closes the stream for good (e.g. 401 after logout elsewhere); normal drops auto-reconnect
const HARD_RECONNECT_MIN_MS = 15000;
const HARD_RECONNECT_JITTER_MS = 30000;
// Snapshot reloads are spread randomly so a deploy or Redis blip doesn't send every tab to the API in the same second
const RECONNECT_SNAPSHOT_JITTER_MS = 5000;
const RESYNC_SNAPSHOT_JITTER_MS = 10000;
const VISIBILITY_SNAPSHOT_JITTER_MS = 1000;
const CLEARED_SNAPSHOT_JITTER_MS = 2000;
// Coming back to a tab within this window of the last good snapshot doesn't refetch — the stream kept it current
const VISIBILITY_FRESH_MS = 30000;

const isVisible = () => typeof document === "undefined" || document.visibilityState === "visible";
// Visible isn't enough: a tab stays "visible" while its window sits behind another app (e.g. the editor), so also require focus
const isAttending = () => isVisible() && (typeof document === "undefined" || document.hasFocus());

export function WorkflowAttentionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AttentionNotification[]>([]);
  const [muted, setMutedState] = useState<boolean>(readMuted);
  const activeProjectId = projectIdFromPath(location.pathname);

  // Refs let the long-lived EventSource handlers read current values without reconnecting
  const activeProjectRef = useRef(activeProjectId);
  const mutedRef = useRef(muted);
  const itemsRef = useRef(notifications);
  activeProjectRef.current = activeProjectId;
  mutedRef.current = muted;
  itemsRef.current = notifications;

  const commit = useCallback((next: AttentionNotification[]) => {
    const nextKeys = new Set(next.map((n) => n.eventKey));
    for (const prev of itemsRef.current) {
      if (!nextKeys.has(prev.eventKey)) toast.dismiss(prev.eventKey);
    }
    itemsRef.current = next;
    setNotifications(next);
  }, []);

  const ackProject = useCallback(
    (projectId: string) => {
      const hasUnread = itemsRef.current.some((n) => n.type === "completed" && n.projectId === projectId);
      if (!hasUnread) return;
      commit(itemsRef.current.filter((n) => !(n.type === "completed" && n.projectId === projectId)));
      axiosInstance.post("/notifications/ack", { project_id: projectId }).catch(() => {
        // Next snapshot brings it back and the ack is retried on the next visit
      });
    },
    [commit],
  );

  const fetchSnapshot = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/notifications");
      commit(fromSnapshot(res.data?.notifications));
      const active = activeProjectRef.current;
      if (active && isAttending()) ackProject(active);
    } catch {
      // Keep the current list; the next ready/resync/visibility event retries
    }
  }, [commit, ackProject]);

  const openNotification = useCallback(
    (n: AttentionNotification) => {
      toast.dismiss(n.eventKey);
      // The question may have been answered in another tab while this one held it; re-check so a stale item drops at once
      if (n.type === "input_required") void fetchSnapshot();
      navigate(`/projects/${n.projectId}`);
    },
    [navigate, fetchSnapshot],
  );

  const snapshotTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastSnapshotAtRef = useRef(0);

  // Coalesces every trigger (ready/resync/visibility) into at most one pending fetch, fired after a random delay
  const requestSnapshot = useCallback(
    (maxDelayMs: number) => {
      if (snapshotTimerRef.current !== undefined) return;
      snapshotTimerRef.current = setTimeout(() => {
        snapshotTimerRef.current = undefined;
        lastSnapshotAtRef.current = Date.now();
        void fetchSnapshot();
      }, Math.random() * maxDelayMs);
    },
    [fetchSnapshot],
  );

  const clearAll = useCallback(() => {
    commit([]);
    axiosInstance.post("/notifications/clear").catch(() => {
      // Nothing was cleared server-side, so bring the list back rather than hide it until the next reload
      requestSnapshot(0);
    });
  }, [commit, requestSnapshot]);

  const announce = useCallback(
    (n: AttentionNotification) => {
      if (!claimEffect(n.eventKey)) return;
      toast(displayTitle(n), {
        id: n.eventKey,
        description: attentionLine(n) || undefined,
        // Action-required stays until resolved; resolution dismisses it via commit()
        duration: n.type === "input_required" ? Infinity : 8000,
        action: { label: "Open", onClick: () => openNotification(n) },
      });
      if (!mutedRef.current) playChime(n.type === "input_required" ? "input" : "completed");
    },
    [openNotification],
  );

  const handleEvent = useCallback(
    (event: AttentionStreamEvent) => {
      const before = itemsRef.current;
      const after = applyEvent(before, event);
      if (after === before) return;
      commit(after);
      // Cleared in another tab; re-read in case something new was raised between that clear and its push
      if (event.type === "cleared") requestSnapshot(CLEARED_SNAPSHOT_JITTER_MS);
      if (event.type !== "input_required" && event.type !== "completed") return;
      const added = after.find((n) => n.eventKey === event.eventKey);
      if (!added) return;
      const looking = isAttending() && activeProjectRef.current === added.projectId;
      if (looking) {
        // The page already shows the question/result; just keep the record (and settle completions)
        if (added.type === "completed") ackProject(added.projectId);
        return;
      }
      announce(added);
    },
    [commit, ackProject, announce, requestSnapshot],
  );

  const userId = isAuthenticated ? user?.id : undefined;

  useEffect(() => {
    if (!userId) {
      commit([]);
      return;
    }
    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;
    let connectedBefore = false;

    const connect = () => {
      if (disposed) return;
      es = new EventSource(`${baseURL}/notifications/stream`, { withCredentials: true });
      // `ready` fires on every (re)connect, so any event missed while disconnected is recovered from the snapshot
      es.addEventListener("ready", () => {
        requestSnapshot(connectedBefore ? RECONNECT_SNAPSHOT_JITTER_MS : 0);
        connectedBefore = true;
      });
      es.addEventListener("resync", () => requestSnapshot(RESYNC_SNAPSHOT_JITTER_MS));
      es.addEventListener("notification", (e) => {
        try {
          handleEvent(JSON.parse((e as MessageEvent).data));
        } catch {
          // Malformed frame; the next snapshot corrects state
        }
      });
      es.onerror = () => {
        if (es?.readyState === EventSource.CLOSED) {
          es.close();
          retryTimer = setTimeout(connect, HARD_RECONNECT_MIN_MS + Math.random() * HARD_RECONNECT_JITTER_MS);
        }
      };
    };
    connect();

    const onVisible = () => {
      if (!isVisible()) return;
      // Pending questions are what go stale when answered in another tab, so they always re-check on return
      const holdsInput = itemsRef.current.some((n) => n.type === "input_required");
      if (holdsInput || Date.now() - lastSnapshotAtRef.current > VISIBILITY_FRESH_MS) requestSnapshot(VISIBILITY_SNAPSHOT_JITTER_MS);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      clearTimeout(snapshotTimerRef.current);
      snapshotTimerRef.current = undefined;
      es?.close();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [userId, requestSnapshot, handleEvent, commit]);

  // Opening (or returning to) a project settles its completion notification
  useEffect(() => {
    if (!activeProjectId) return;
    const settle = () => {
      if (isAttending()) ackProject(activeProjectId);
    };
    settle();
    document.addEventListener("visibilitychange", settle);
    window.addEventListener("focus", settle);
    return () => {
      document.removeEventListener("visibilitychange", settle);
      window.removeEventListener("focus", settle);
    };
  }, [activeProjectId, ackProject]);

  // Title and favicon only flag the tab showing that project; the bell and toast cover every other page
  const tabItems = useMemo(
    () => (activeProjectId ? notifications.filter((n) => n.projectId === activeProjectId) : []),
    [notifications, activeProjectId],
  );
  useAttentionIndicators(tabItems);

  const setMuted = useCallback((next: boolean) => {
    writeMuted(next);
    setMutedState(next);
  }, []);

  const value = useMemo(
    () => ({ notifications, muted, setMuted, openNotification, clearAll }),
    [notifications, muted, setMuted, openNotification, clearAll],
  );

  return <WorkflowAttentionContext.Provider value={value}>{children}</WorkflowAttentionContext.Provider>;
}

export function useWorkflowAttention(): WorkflowAttentionValue {
  return useContext(WorkflowAttentionContext);
}
